/**
 * "Descarcă" that actually lets a phone keep the file.
 *
 * On iOS, in the installed web app, a link to a PDF opens Safari's bare
 * viewer: no share button, no "Save to Files", and the only way back is the
 * app switcher. The storage proxy serves files `inline`, so the desktop gets
 * a tab with the PDF, which is what it wants — but a phone needs the native
 * share sheet, which is where "Save to Files", AirDrop and Mail live.
 *
 * So: fetch the bytes, wrap them in a File, and hand it to the Web Share API
 * when the browser can share files and the device is touch-first. Everywhere
 * else, a blob URL with the `download` attribute saves the file directly,
 * and if even that is unavailable the link opens as before.
 */

function mobileLike(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

function shareTarget(file: File): boolean {
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  return typeof nav.share === 'function' && typeof nav.canShare === 'function'
    ? nav.canShare({ files: [file] })
    : false;
}

async function fetchAsFile(url: string, name: string): Promise<File> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Descărcarea a eșuat (${res.status})`);
  const blob = await res.blob();
  const type = blob.type || res.headers.get('content-type') || 'application/octet-stream';
  return new File([blob], name, { type });
}

function downloadBlob(file: File): void {
  const href = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = href;
  a.download = file.name;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/**
 * Save or share a stored file. Resolves when the share sheet closed or the
 * download started; a cancelled share sheet is not an error.
 */
export async function saveFile(url: string, name: string): Promise<void> {
  let file: File;
  try {
    file = await fetchAsFile(url, name);
  } catch {
    // Can't read the bytes here (blocked fetch, expired link): fall back to
    // the plain link so the user still gets the server's answer.
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  if (mobileLike() && shareTarget(file)) {
    try {
      await navigator.share({ files: [file], title: name });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return; // user closed the sheet
      // Sharing failed for another reason: fall through to the download.
    }
  }
  downloadBlob(file);
}
